import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { EventTypeService } from '../../../../../../services/event-type/event-type.service';

import { EventType, EventTypeLocationSettings} from '../../../../../../models/event-type';
import { AccountService } from '../../../../../../services/account/account.service';
import { LocationService } from '../../../../../../services/location/location.service';
import { EventTypeStatus } from '../../../../../../models/event-type-enums';
import { DocumentTemplate } from '../../../../../../models/document-template';
import { DocumentTemplateService } from '../../../../../../services/document-template/document-template.service';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';

@Component({
  selector: 'app-event-type-emails',
  templateUrl: './event-type-emails.component.html',
  styleUrls: ['./event-type-emails.component.css']
})
export class EventTypeEmailsComponent implements OnInit, ComponentHasUnsavedChanges {

  public readonly SUBMIT_TYPE_STAY_HERE = 'stayHere';
  public readonly SUBMIT_TYPE_CONTINUE = 'continue';

  public eventType: EventType = null;
  public eventTypeLocationSettings: EventTypeLocationSettings[] = null;
  public submitType = this.SUBMIT_TYPE_STAY_HERE;
  public bookingStaffNotificationSelected = {};

  public documentTemplates: DocumentTemplate[] = null;

  // Options for the waiver reminder email template dropdown: active templates,
  // plus any already-configured-but-retired template so the selection isn't
  // silently lost (PRO-863).
  public waiverEmailTemplateOptions:
    { id: number; name: string; isRetired: boolean }[] = [];

  public selectedLocationSettings: EventTypeLocationSettings = null;

  public errorMessage = '';
  public errors: any = {};
  public successMessage = '';
  public unsavedChanges = false;

  public loadingEventType = true;
  public loadingLocationSettings = true;
  public loadingDocumentTemplates = true;
  public loadingSave = false;

  public eventTypeNotFound = false;
  public serverError: number = null;

  constructor(
    public locationService: LocationService,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private documentTemplateService: DocumentTemplateService,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    const eventTypeIdInt = parseInt(routeParams.id, 10);
    if (isNaN(eventTypeIdInt)) {
      this.eventTypeNotFound = true;
      return;
    }

    this.loadEventType(eventTypeIdInt);
    this.loadEventTypeLocationSettings(eventTypeIdInt);
    this.loadDocumentTemplates();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Whether there are any outstanding API requests in progress.
   */
  public isLoadingSubmit() {
    return this.loadingSave;
  }

  public isLoadingInitial(): boolean {
    return (
      (this.eventType === null) ||
      (this.eventTypeLocationSettings === null) ||
      (this.locationService.getLocations() === null)
    )
  }

  /**
   * Handle a form submission.
   */
  public onSubmit() {
    this.loadingSave = true;

    this.errorMessage = '';
    this.errors = {};
    this.successMessage = '';

    if (this.bookingStaffNotificationSelected[this.selectedLocationSettings.id]) {
      if (!this.selectedLocationSettings.staff_notification_email_addresses) {
        this.errors = {
          staff_notification_email_addresses: 'This field is required.'
        };
        this.errorMessage = 'Please correct the errors above and try again.';
        this.loadingSave = false;
        return;
      }
    } else {
      this.selectedLocationSettings.staff_notification_email_addresses = '';
    }

    this.eventTypeService
      .updateEventTypeLocationSettings(this.selectedLocationSettings)
      .then(updatedEventType => {
        this.unsavedChanges = false;

        // Routing depends on which save button was clicked.
        if (this.submitType === this.SUBMIT_TYPE_STAY_HERE) {
          this.successMessage = updatedEventType.location_name + ' settings updated successfully.';
        } else if (this.submitType === this.SUBMIT_TYPE_CONTINUE) {
          this.router.navigate(
            ['/events/setup/edit/' + this.eventType.id.toString() + '/cancellation-policy']
          );
        }
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to modify this event.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSave = false; });
  }

  public handleLocationChange() {
    if (!this.unsavedChanges) { return; }

    if (confirm('Are you sure you wish to change the location being edited? Your previous changes have not been saved.')) {
      this.unsavedChanges = false;
    } else {
      const preChangeSelectedLocationSettings = this.selectedLocationSettings;
      setTimeout(
        () => {
          this.selectedLocationSettings = preChangeSelectedLocationSettings;
        },
        100
      )
    }
  }

  /**
   * Load the event type with the given ID.
   *
   * @param eventTypeId - The API ID of the event type.
   */
  private loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  private loadEventTypeLocationSettings(eventTypeId: number) {
    this.loadingLocationSettings = true;

    this.eventTypeService
      .getEventTypeLocationSettings(eventTypeId, 1, 3000)
      .then(result => {
        this.eventTypeLocationSettings = result.results;
        this.selectedLocationSettings = this.eventTypeLocationSettings[0];
        for (const thisLocSettings of this.eventTypeLocationSettings) {
          this.bookingStaffNotificationSelected[thisLocSettings.id] = !!thisLocSettings.staff_notification_email_addresses;
        }
        this.buildWaiverEmailTemplateOptions();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingLocationSettings = false; });
  }

  private loadDocumentTemplates() {
    this.loadingDocumentTemplates = true;

    this.documentTemplateService
        .getDocumentTemplates(1, 3000)
        .then(result => {
          this.documentTemplates = result.results
              .sort((a, b) => {
                return (a.name > b.name) ? 1 : -1;
              });
          this.buildWaiverEmailTemplateOptions();
        })
        .catch(reason => {
          this.serverError = reason.status;
        })
        .finally(() => { this.loadingDocumentTemplates = false; });
  }

  /**
   * Build the waiver reminder email template dropdown options: the active
   * templates, plus any retired template already configured on a location so
   * its selection stays visible (and keeps working) until replaced (PRO-863).
   * Runs once both the templates and location settings have loaded.
   */
  private buildWaiverEmailTemplateOptions() {
    if ((this.documentTemplates === null) ||
        (this.eventTypeLocationSettings === null)) {
      return;
    }

    const options = this.documentTemplates.map(dt => ({
      id: dt.id,
      name: dt.name,
      isRetired: false
    }));
    const activeIds = new Set(this.documentTemplates.map(dt => dt.id));

    const addedRetiredIds = new Set<number>();
    for (const locSettings of this.eventTypeLocationSettings) {
      const id = locSettings.waiver_email_document_template_id;
      if ((id !== null) && !activeIds.has(id) && !addedRetiredIds.has(id)) {
        addedRetiredIds.add(id);
        options.push({
          id,
          name: locSettings.waiver_email_document_template_name,
          isRetired: true
        });
      }
    }

    this.waiverEmailTemplateOptions = options;
  }

}
